import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addEditLoyaltyPrograms,
  organizationSupplierLoyaltyCompanies,
} from '../api';
import { useAuth } from '../hooks/useAuth';
import { ILoyaltyCompanies, ILoyaltyPrograms } from '../utils/types';

interface AddEditLoyaltyProgramDialogProps {
  open: boolean;
  onClose: () => void;
  program?: ILoyaltyPrograms;
  onSave: (program: ILoyaltyPrograms) => void;
}

const AddEditLoyaltyProgramDialog: React.FC<
  AddEditLoyaltyProgramDialogProps
> = ({ open, onClose, program, onSave }) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [organizationLoyaltyCompanies, setOrganizationLoyaltyCompanies] =
    useState<ILoyaltyCompanies[]>([]);

  const [programData, setProgramData] = useState<ILoyaltyPrograms>({
    id: 0,
    company: { id: 0, name: '' },
    membershipNumber: '',
  });

  useEffect(() => {
    if (program && open) {
      setProgramData(program);
    } else if (!program && open) {
      setProgramData({
        id: 0,
        company: { id: 0, name: '' },
        membershipNumber: '',
      });
    }
  }, [program, open]);

  useEffect(() => {
    if (open) {
      fetchOrganizationSupplierLoyaltyCompanies();
    }
  }, [open]);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>,
  ) => {
    const { name, value } = e.target;

    if (name === 'companyId') {
      const selectedCompany = organizationLoyaltyCompanies.find(
        (company) => company.id === parseInt(value),
      );
      setProgramData((prevData) => ({
        ...prevData,
        company: selectedCompany || { id: 0, name: '' },
      }));
    } else {
      setProgramData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const fetchOrganizationSupplierLoyaltyCompanies = async () => {
    try {
      const res = await organizationSupplierLoyaltyCompanies(token);
      const data = await res.json();

      if (res?.status === 200) {
        setOrganizationLoyaltyCompanies(data);
      } else {
        throw new Error(data.detail);
      }
    } catch (e: any) {
      setError(e.message);
      console.error('Fetch Loyalty Programs Error:', e);
    }
  };

  const fetchAddEditLoyaltyProgram = async () => {
    try {
      setLoading(true);
      const res = await addEditLoyaltyPrograms(
        programData.id,
        token,
        programData.company.id,
        programData.membershipNumber,
      );

      if (res?.status !== 200 && res?.status !== 201) {
        const data = await res.json();
        throw new Error(data.detail);
      }
    } catch (e: any) {
      setError(e.message);
      console.error('Add/Edit Loyalty Program Error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    await fetchAddEditLoyaltyProgram();
    onSave(programData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t(
          program?.id
            ? t('accountInfosPage.editLoyaltyProgram')
            : t('accountInfosPage.addLoyaltyProgram'),
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>{t('accountInfosPage.loyaltyProgram')}</InputLabel>
            <Select
              name="companyId"
              label={t('accountInfosPage.loyaltyProgram')}
              value={programData.company.id.toString()}
              onChange={handleChange}
              fullWidth
              size="small"
            >
              {organizationLoyaltyCompanies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('accountInfosPage.membershipNumber')}
            name="membershipNumber"
            value={programData.membershipNumber}
            onChange={handleChange}
            fullWidth
            size="small"
          />
          {error && <Box sx={{ color: 'red' }}>{error}</Box>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error">
          {t('accountInfosPage.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {t('accountInfosPage.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditLoyaltyProgramDialog;
